import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, useNotifications } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ArchivePartnerListDocument, PartnerListsDocument } from '../../graphql/__generated__/types';
import { partnerListRefusalOf } from './refusals';

interface Props {
    partnerListId: string;
    name: string;
    holderCount: number;
    onClose: () => void;
}

// The blocked state is rendered before the call from a count the screen already holds;
// the server still refuses, and a stale count lands on PARTNER_LIST_HAS_HOLDERS below.
const ArchivePartnerListDialog = ({ partnerListId, name, holderCount, onClose }: Props) => {
    const { createNotification } = useNotifications();
    const navigate = useNavigate();
    const blocked = holderCount > 0;

    const [archive, { loading, error }] = useMutation(ArchivePartnerListDocument, {
        refetchQueries: [PartnerListsDocument],
        awaitRefetchQueries: true,
    });

    const submit = async () => {
        try {
            await archive({ variables: { partnerListId, reason: null } });
            createNotification({
                type: 'success',
                text: c('Partner lists').t`List « ${name} » archived`,
            });
            onClose();
            navigate('/listes-de-partenaires');
        } catch {
            // Rendered from `error` below.
        }
    };

    if (blocked) {
        return (
            <Modal open onClose={onClose} title={c('Partner lists').t`Archiving is not possible`} size={760}>
                <ModalContent>
                    <Alert type="warning">
                        <p className="font-semibold">
                            {c('Partner lists').ngettext(
                                msgid`You cannot archive the list « ${name} »: ${holderCount} user is attached to it.`,
                                `You cannot archive the list « ${name} »: ${holderCount} users are attached to it.`,
                                holderCount
                            )}
                        </p>
                        <p className="mt-1">
                            {c('Partner lists')
                                .t`Give them another list from their own user sheet, then archive this one. Somebody who has left the company counts here too, until they are detached.`}
                        </p>
                    </Alert>
                </ModalContent>
                <ModalFooter>
                    <Button onClick={onClose}>{c('Partner lists').t`Understood`}</Button>
                </ModalFooter>
            </Modal>
        );
    }

    return (
        <Modal open onClose={onClose} title={c('Partner lists').t`Archive this list?`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-3 text-sm">
                    <p>
                        {c('Partner lists')
                            .t`« ${name} » leaves the catalogue. A list is never deleted, and nothing brings an archived one back.`}
                    </p>
                    <p className="text-text-600">
                        {c('Partner lists')
                            .t`Its name stays reserved for ever, and no screen lists the archived ones: the theme cannot be used again. Its partners keep their PTN identifiers and stay in every other list they belong to.`}
                    </p>
                    {error && <RefusalNotice refusal={partnerListRefusalOf(error)} />}
                </div>
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Partner lists').t`Cancel`}
                </Button>
                <Button category="destructive" onClick={submit} loading={loading}>
                    {c('Partner lists').t`Archive the list`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ArchivePartnerListDialog;
